"use client";

import { apiRemote, IDashboardUserCourse } from "@/api";
import he from 'he';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  LinearProgress,
  Pagination,
  PaginationItem,
  IconButton,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDashboardContext } from "./Layout";
import logo from '../../images/imgcourse.png';
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";

// Custom SVG icon for the course
const CourseIcon = () => (
  <svg
    width="100"
    height="100"
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="100" height="100" rx="4" fill="#1a237e" />
    <path d="M30 25h40v35h-40z" fill="#fff" />
    <path d="M35 65h30v10h-30z" fill="#fff" />
    <circle cx="50" cy="42" r="15" fill="#1a237e" />
    <path d="M45 42l5 5 8-8" stroke="#fff" strokeWidth="2" />
  </svg>
);

// Custom right chevron icon
const ChevronRightIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 18L15 12L9 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const decodeHTMLEntities = (text: any) => {
  const textArea = document.createElement("textarea")
  textArea.innerHTML = text
  return textArea.value
}

export interface IDashboardUserCourseExtend extends IDashboardUserCourse {}

export default function MyCourses() {
  const [courses, setCourses] = useState<IDashboardUserCourse["data"]["course_details"]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [pageSize, setPageSize] = useState(4);
  const { getDashboardUserCourse } = useDashboardContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchCourses = async () => {
      setLoading(true); // Bắt đầu tải dữ liệu
      try {
        
        const res = await getDashboardUserCourse(currentPage.toString(), pageSize.toString());
        setCourses(res.data.course_details);
        setTotalPages(Number(res.data.totalpage));
        setCount(res.data.course_details.length);
      } catch (error) {
        console.error("Error fetching badges:", error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchCourses();
  }, [currentPage, pageSize]);

  useEffect(() => {
    if(isMobile){
      setPageSize(1)
    }else{
      setPageSize(4)
    }
  }, [isMobile]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };
  
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "white",
        borderRadius: 2,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        mt: 4,
        flex: 1,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: "rgb(15, 105, 185)",
          fontWeight: 500,
          mb: 3,
        }}
      >
        Enrolled Courses
      </Typography>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 3,
          }}
        >
          <CircularProgress />
        </Box>
      ) : count === 0 ?  (
        <Typography
          sx={{
            color: "rgb(102, 102, 102)",
            fontSize: "1rem",
            textAlign: "center",
          }}
        >
          No data available
        </Typography>
      ) : (
        <>
          <Box sx={{ position: "relative", mb: 3 }}>
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {courses.map((course: any) => (
                <Grid item xs={12} sm={6} md={3} key={course.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      bgcolor: "rgb(247, 250, 255)",
                      border: "1px solid rgb(237, 242, 247)",
                      boxShadow: "none",
                      borderRadius: 2,
                    }}
                  >
                    <CardMedia
                      component="div"
                      sx={{
                        pt: "56.25%",
                        position: "relative",
                        bgcolor: "white",
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          width: '100%',
                          height: '100%'
                        }}
                      >
                        <img
                          src={course.course_image || logo}
                          alt={course.coursename.toString()}
                          width="100%"
                          height="100%"
                          style={{
                            objectFit: 'cover'
                          }}
                        />
                      </Box>
                    </CardMedia>
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          mb: 2,
                          fontSize: '16px',
                          fontWeight: 600,
                          lineHeight: '24px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          height: '48px',
                          bgcolor: 'transparent'
                        }}
                      >
                        {he.decode(course.coursename)}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            mb: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {course.summary ? decodeHTMLEntities(course.summary.replace(/<[^>]*>/g, "")) : ""}
                          {/* {course.summary?.replace(/<[^>]*>/g, '')} */}
                        </Typography>
                      </Box>

                      <Box sx={{ mt: "auto" }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          {course.total_activity_completion} out of{" "}
                          {course.total_activity} activities completed
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={course.completionPercentage}
                          sx={{
                            mb: 1,
                            height: 8,
                            borderRadius: 4,
                            bgcolor: "rgb(237, 242, 247)",
                            "& .MuiLinearProgress-bar": {
                              bgcolor: "rgb(82, 196, 26)",
                            },
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          {course.completionPercentage}% Course Completed
                        </Typography>
                        <Button
                          href={course.view_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          fullWidth
                          variant="contained"
                          sx={{
                            bgcolor: "rgb(255, 90, 31)",
                            "&:hover": {
                              bgcolor: "rgb(230, 81, 28)",
                            },
                          }}
                        >
                          Go
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <IconButton
              onClick={handlePrevPage} // Chuyển trang về trước
              sx={{
                position: "absolute",
                left: -16,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "white",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                "&:hover": {
                  bgcolor: "rgb(247, 250, 255)",
                },
              }}
            >
              <ArrowBackIos />
            </IconButton>
            
            <IconButton
              onClick={handleNextPage} // Chuyển trang tới tiếp theo
              sx={{
                position: "absolute",
                right: -16,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "white",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                "&:hover": {
                  bgcolor: "rgb(247, 250, 255)",
                },
              }}
            >
              <ArrowForwardIos />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              shape="rounded"
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  sx={{
                    "&.Mui-selected": {
                      bgcolor: "rgb(15, 105, 185)",
                      color: "white",
                      "&:hover": {
                        bgcolor: "rgb(13, 94, 166)",
                      },
                    },
                  }}
                />
              )}
            />
          </Box>
        </>
      )}
    </Box>
  );
}
